import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { SectionHeading } from '@/components/base/SectionHeading';
import { brandCategories } from '@/mocks/products';
import { articles } from '@/mocks/articles';
import { brandInfo, faqItems } from '@/mocks/brand';
import { allProducts } from '@/mocks/products';
import { useState } from 'react';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredArticles = articles.slice(0, 3);
  const featuredProducts = allProducts.slice(0, 6);

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '幼水若若',
            url: 'https://ruoruworld.github.io/',
            description: brandInfo.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://ruoruworld.github.io/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />

      {/* Hero */}
      <section className="relative w-full h-[580px] md:h-[680px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dreamy%20soft%20abstract%20water%20texture%20in%20warm%20cream%20and%20soft%20terracotta%20tones%2C%20flowing%20organic%20shapes%20like%20gentle%20water%20ripples%2C%20delicate%20light%20reflections%2C%20ethereal%20and%20minimalist%20background%2C%20smooth%20gradients%2C%20elegant%20and%20serene%20artistic%20composition&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
            alt="幼水若若 - 以柔克刚"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/30"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-6">
          <span className="text-xs md:text-sm tracking-[0.3em] text-white/80 uppercase font-accent">
            Youshui Ruoruo
          </span>
          <h1 className="mt-4 text-4xl md:text-6xl lg:text-7xl font-heading text-white tracking-widest">
            幼水若若
          </h1>
          <p className="mt-6 text-sm md:text-base text-white/80 max-w-lg text-center leading-relaxed tracking-wide">
            以柔克刚，如幼水般温润流淌
            <br />
            让生活回归本真的温柔与自然
          </p>
          <div className="mt-10 flex items-center gap-4">
            <Link
              to="/category/ruoruo-series"
              className="px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm tracking-wide whitespace-nowrap hover:bg-primary-600 transition-all duration-300"
            >
              探索产品
            </Link>
            <Link
              to="/about"
              className="px-7 py-3 rounded-full border border-white/40 text-white text-sm tracking-wide whitespace-nowrap hover:bg-white/10 transition-all duration-300"
            >
              了解品牌
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <i className="ri-arrow-down-line text-white/50 text-2xl"></i>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 md:py-28 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            tagline="Our Philosophy"
            title="品牌理念"
            description="水，无形却有万形。以最柔软的姿态，成就最坚韧的力量。"
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {brandInfo.philosophy.items.map((item, index) => (
              <div
                key={index}
                className="group p-8 rounded-lg bg-background-100 hover:bg-background-200/70 transition-all duration-300 text-center"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-accent-100/80 flex items-center justify-center">
                  <i className={`${item.icon} text-xl text-accent-600`}></i>
                </div>
                <h3 className="mt-5 text-base font-heading text-foreground-800">
                  {item.title}
                </h3>
                <p className="mt-2.5 text-sm text-foreground-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            tagline="Our Products"
            title="产品系列"
            description="三个系列，三种水润之美。找到属于你的那一份温柔。"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {brandCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group block p-8 rounded-lg bg-background-50 hover:bg-primary-50/50 transition-all duration-300"
              >
                <div className="w-full h-48 rounded-md overflow-hidden">
                  <img
                    src={cat.products[0].image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="mt-5 text-lg font-heading text-foreground-800 group-hover:text-primary-500 transition-colors duration-200">
                  {cat.name}
                </h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                  {cat.description}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-primary-500">
                  查看系列 <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 md:py-28 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            tagline="Featured"
            title="明星产品"
            description="用心甄选，为你的肌肤送上最温柔的呵护。"
          />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {featuredProducts.map((product) => (
              <div
                key={product.id}
                className="group cursor-pointer"
              >
                <div className="w-full aspect-square rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h4 className="mt-3 text-sm font-medium text-foreground-800 leading-tight group-hover:text-primary-500 transition-colors">
                  {product.name}
                </h4>
                <p className="mt-1 text-xs text-foreground-400 line-clamp-2">
                  {product.tagline}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/category/ruoruo-series"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-background-300 text-sm text-foreground-600 whitespace-nowrap hover:border-primary-300 hover:text-primary-500 transition-all duration-300"
            >
              查看全部产品 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            tagline="Inspiration"
            title="灵感笔记"
            description="分享品牌故事、护肤指南与生活灵感。"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-200/30 transition-all duration-300"
              >
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span>{article.category}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                  </div>
                  <h3 className="mt-2 text-base font-heading text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary-500">
                    阅读全文 <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-background-300 text-sm text-foreground-600 whitespace-nowrap hover:border-primary-300 hover:text-primary-500 transition-all duration-300"
            >
              阅读更多 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Story Teaser */}
      <section className="relative py-28 md:py-36 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20serene%20minimalist%20composition%2C%20a%20single%20water%20droplet%20falling%20into%20a%20still%20body%20of%20water%20creating%20gentle%20ripples%2C%20warm%20soft%20lighting%20with%20cream%20and%20beige%20tones%2C%20abstract%20and%20artistic%2C%20meditative%20and%20peaceful%20atmosphere&width=1600&height=900&seq=brand-story-teaser&orientation=landscape"
            alt="品牌故事"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-black/25"></div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <span className="text-xs tracking-[0.3em] text-white/70 uppercase font-accent">
            Our Story
          </span>
          <h2 className="mt-3 text-2xl md:text-4xl font-heading text-white tracking-wide">
            从一滴水开始的品牌旅程
          </h2>
          <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-lg mx-auto">
            三年前的春天，创始人在云南茶山被一滴露水打动。那一刻，她看见了水最纯粹的力量——温柔而坚韧，这正是幼水若若的起点。
          </p>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm tracking-wide whitespace-nowrap border border-white/20 hover:bg-white/25 transition-all duration-300"
          >
            了解更多 <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <SectionHeading
            tagline="FAQ"
            title="常见问题"
            description="关于幼水若若的常见疑问，在这里找到答案。"
          />
          <div className="mt-12 flex flex-col gap-3">
            {faqItems.map((faq, index) => (
              <div
                key={index}
                className="rounded-lg bg-background-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left group cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-700 pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 pb-4' : 'max-h-0'
                  }`}
                >
                  <p className="px-6 text-sm text-foreground-500 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-6 md:px-10 bg-primary-500">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading text-background-50 tracking-wide">
            开始你的温柔护肤之旅
          </h2>
          <p className="mt-3 text-sm text-background-50/80 leading-relaxed">
            探索幼水若若全系列产品，找到最适合你的那一份水润温柔。
          </p>
          <Link
            to="/category/ruoruo-series"
            className="mt-7 inline-flex items-center gap-2 px-8 py-3 rounded-full bg-background-50 text-primary-600 text-sm font-medium tracking-wide whitespace-nowrap hover:bg-background-100 transition-all duration-300"
          >
            探索产品系列 <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </section>

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '幼水若若',
            url: 'https://ruoruworld.github.io/',
            description: brandInfo.description,
            foundingDate: '2023',
            address: {
              '@type': 'PostalAddress',
              addressLocality: '杭州',
              addressCountry: 'CN',
            },
            contactPoint: {
              '@type': 'ContactPoint',
              email: brandInfo.contact.email,
              telephone: brandInfo.contact.phone,
              contactType: 'customer service',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </Layout>
  );
}